import { ApiService } from "./api";

export interface VerificationInputField {
  inputFieldId: number;
  value?: string;
  mediaId?: number | string;
}

export interface CreateVerificationRequest {
  templateId: number;
  title: string;
  description: string;
  senderContext: number;
  senderOrganizationId?: number;
  receiverContext: number;
  receiverOrganizationId?: number;
  input_field_values: VerificationInputField[];
  assigneeIds: number[];
}

// Without these the backend labels requests from Outlook as "App"
const OUTLOOK_SOURCE = { source: "WIDGET" as const, sourceName: "Outlook" };

export interface VerificationResponse {
  id: number;
  templateId: number;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export class VerificationService {
  private static ENDPOINT = "/verification-requests";

  static async create(data: CreateVerificationRequest, token: string) {
    return await ApiService.post<VerificationResponse>(
      this.ENDPOINT,
      { ...data, ...OUTLOOK_SOURCE },
      token
    );
  }

  static async createWithFiles(
    data: CreateVerificationRequest,
    files: { fieldId: number; name: string; base64: string; mimeType: string }[],
    token: string
  ) {
    const formData = new FormData();

    formData.append("templateId", String(data.templateId));
    formData.append("title", data.title);
    formData.append("description", data.description);
    // formData.append("senderContext", String(data.senderContext));
    if (data.senderOrganizationId !== undefined) {
      formData.append("senderOrganizationId", String(data.senderOrganizationId));
    }
    // formData.append("receiverContext", String(data.receiverContext));
    if (data.receiverOrganizationId !== undefined) {
      formData.append("receiverOrganizationId", String(data.receiverOrganizationId));
    }
    formData.append("assigneeIds", JSON.stringify(data.assigneeIds));
    formData.append("input_field_values", JSON.stringify(data.input_field_values));
    formData.append("source", OUTLOOK_SOURCE.source);
    formData.append("sourceName", OUTLOOK_SOURCE.sourceName);

    files.forEach((f) => {
      const bytes = atob(f.base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: f.mimeType });
      formData.append(`files`, blob, f.name);
    });

    return await ApiService.postFormData<VerificationResponse>(this.ENDPOINT, formData, token);
  }

  static async getById(id: string | number, token: string) {
    return await ApiService.get<VerificationResponse>(`${this.ENDPOINT}/${id}`, token);
  }

  // The detail API no longer includes activities; the timeline now comes from the audit log
  static async getAuditLog(id: string | number, token: string) {
    return await ApiService.get<any>(`${this.ENDPOINT}/${id}/audit-log`, token);
  }

  // The list API returns only pending requests by default; completed (approved/rejected)
  // ones come from type=done. Fetch both and merge so callers see the full history.
  static async listAll(token: string) {
    const base = `${this.ENDPOINT}?visibilityContext=widget`;
    const [pending, done] = await Promise.all([
      ApiService.get<any>(base, token),
      ApiService.get<any>(`${base}&type=done`, token),
    ]);

    if (!pending.data && !done.data) return pending;

    const toList = (res: { data?: any }) => {
      const body = res.data;
      const list = Array.isArray(body) ? body : body?.data;
      return Array.isArray(list) ? list : [];
    };
    const merged = [...toList(pending), ...toList(done)]
      .filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return { data: { success: true, data: merged }, status: pending.data ? pending.status : done.status };
  }
}
